import { FaSortAmountDown, FaSortAmountUp, FaSpinner } from "react-icons/fa";
import { Filter } from "../../Models/Filter";
import "./DocumentsTable.css"
import { useSearchParams } from "react-router-dom";
import { SortByType, SortInfo } from "../../Models/SortInfo";
import UserFile from "../../Models/UserFile";
import UserFileService from "../../Services/UserFileService";
import { useEffect, useState } from "react";

interface DocumentsTableProps {
    files: UserFile[];
    filtredFiles: UserFile[];
    selectedFiles: UserFile[];
    setSelectedFiles: React.Dispatch<React.SetStateAction<UserFile[]>>;
    setFiltredFiles: React.Dispatch<React.SetStateAction<UserFile[]>>;
    setFiles: React.Dispatch<React.SetStateAction<UserFile[]>>;
    setTokenExpired: React.Dispatch<React.SetStateAction<boolean>>;
    currentPage: number;
    pageSize: number
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
    files,
    filtredFiles,
    selectedFiles,
    setSelectedFiles,
    setFiltredFiles,
    setFiles,
    setTokenExpired,
    currentPage,
    pageSize
}) => {
    // Get User Token from SearchParams
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Loading and error states
    const [loadingFileId, setLoadingFileId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Files sort infos
    const [sortInfos, setSortInfos] = useState<SortInfo>({
        currentSortBy: SortByType.CreationDate,
        orders: {},
    });

    // Sort Files Liste
    useEffect(() => {
        const ascendingOrder = sortInfos.orders[sortInfos.currentSortBy] ?? false;

        const sortedFiles = [...filtredFiles].sort((a, b) => {
            switch (sortInfos.currentSortBy) {
                case SortByType.FileType:
                    return ascendingOrder
                        ? a.fileType.localeCompare(b.fileType)
                        : b.fileType.localeCompare(a.fileType);

                case SortByType.FileName:
                    return ascendingOrder
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);

                case SortByType.CreationDate:
                    const createdA = new Date(a.createdAt).getTime();
                    const createdB = new Date(b.createdAt).getTime();
                    return ascendingOrder ? createdA - createdB : createdB - createdA;

                case SortByType.DueDate:
                    const dueA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                    const dueB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                    return ascendingOrder ? dueA - dueB : dueB - dueA;

                case SortByType.DocumentType:
                    return ascendingOrder
                        ? a.documentType.localeCompare(b.documentType)
                        : b.documentType.localeCompare(a.documentType);

                case SortByType.Entity:
                    return ascendingOrder
                        ? a.entityType.localeCompare(b.entityType)
                        : b.entityType.localeCompare(a.entityType);

                case SortByType.Store:
                    return ascendingOrder
                        ? a.storeName.localeCompare(b.storeName)
                        : b.storeName.localeCompare(a.storeName);

                case SortByType.Status:
                    return ascendingOrder
                        ? Number(a.isRead) - Number(b.isRead)
                        : Number(b.isRead) - Number(a.isRead);

                default:
                    return 0;
            }
        });

        setFiltredFiles(sortedFiles);
        setSelectedFiles(selectedFiles.filter((f) => sortedFiles.includes(f)));
    }, [sortInfos, files]);

    // Toggle sort by data type
    const toggleSort = (field: SortByType) => {
        setSortInfos(prev => {
            const currentOrder = prev.orders[field] ?? false;
            return {
                currentSortBy: field,
                orders: {
                    ...prev.orders,
                    [field]: !currentOrder,
                },
            };
        });
    };

    // Select all filtred Files
    const handleSelectAll = () => {
        if (selectedFiles.length === pageSize) {
            setSelectedFiles([]);
            return;
        }
        setSelectedFiles(filtredFiles.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize));
    }

    // Check if the file is selected
    const isSelected = (file: UserFile) => selectedFiles.some(f => f.id === file.id);

    // Select a single file
    const toggleFileSelection = (file: UserFile) => {
        setSelectedFiles(prev => {
            const alreadySelected = prev.some(f => f.id === file.id);
            if (alreadySelected) {
                return prev.filter(f => f.id !== file.id);
            } else {
                return [...prev, file];
            }
        });
    };

    // Get icone link for each file type
    const getImageLink = (type: String) => {
        switch (type.toLowerCase()) {
            case "xlsx":
            case "xls":
                return "/microsoft-excel-icone.svg";
            case "docx":
                return "/microsoft-word-icone.svg";
            case "csv":
                return "/csv-icone.png";
            case "pdf":
                return "/pdf-icone.png";
            case "txt":
                return "/txt-icone.png"
            default:
                return "/fichier-icone.svg";
        }
    }

    // Open the file in a new tab if it's .txt or .pdf. If not download it
    const handleOpenFile = async (fileInfo: UserFile) => {
        setLoadingFileId(fileInfo.id);
        setError(null);
        
        try {
            const blob = await UserFileService.downloadFiles([fileInfo], token);
            
            if (!blob || blob.size === 0) {
                throw new Error("Le fichier téléchargé est vide.");
            }
            
            const url = URL.createObjectURL(blob);

            const newTab = window.open();
            if (!newTab) {
                throw new Error("Impossible d'ouvrir un nouvel onglet. Vérifiez les paramètres de votre navigateur.");
            }

            newTab.document.title = fileInfo.name;
            if (fileInfo.fileType === 'txt') {
                const reader = new FileReader();
                reader.onload = function () {
                    newTab.document.body.innerHTML = `<pre>${reader.result}</pre>`;
                };
                reader.onerror = function () {
                    newTab.close();
                    throw new Error("Erreur lors de la lecture du fichier texte.");
                };
                reader.readAsText(blob);
            }
            else if (fileInfo.fileType === 'pdf') {
                newTab.location.href = url;
            } else {
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileInfo.name);

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            }
            
            // Change file status
            setFiles(files.map((file) => {
                if (fileInfo === file)
                    file.isRead = true;
                return file;
            }));

        } catch (error: any) {
            console.error("Erreur lors de l'ouverture du fichier :", error);
            
            if (error.status === 401 && files.length !== 0) {
                setTokenExpired(true);
            } else {
                // Provide specific error messages
                let errorMessage = "Erreur lors de l'ouverture du fichier. Veuillez réessayer.";
                
                if (error.status === 404) {
                    errorMessage = "Le fichier est introuvable.";
                } else if (error.status === 403) {
                    errorMessage = "Vous n'avez pas les permissions pour accéder à ce fichier.";
                } else if (error.status === 500) {
                    errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
                } else if (error.message && error.message.includes("vide")) {
                    errorMessage = "Le fichier est vide ou corrompu.";
                } else if (error.message && error.message.includes("onglet")) {
                    errorMessage = error.message;
                } else if (!navigator.onLine) {
                    errorMessage = "Pas de connexion internet. Vérifiez votre connexion.";
                }
                
                setError(errorMessage);
            }
        } finally {
            setLoadingFileId(null);
        }
    };
    return (
        <>
            {error && (
                <div className="Table-Error-Modal">
                    <div className="Table-Error-Content">
                        <span>{error}</span>
                        <button
                            className="Table-Error-Close"
                            onClick={() => setError(null)}
                            aria-label="Fermer"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            <div id="Table-Container">
                <div id="Documents-Table">
                    <table>
                        <thead>
                            <tr>
                                <th className="Table-Header-Select" onClick={handleSelectAll}>{selectedFiles.length === filtredFiles.length ? <>Tout <br />désélectionner</> : <>Tout <br />sélectionner</>}</th>
                                <th onClick={() => toggleSort(SortByType.FileType)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.FileType] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Format</div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.FileName)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.FileName] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Nom du fichier</div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.DueDate)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.DueDate] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Date d'échéance<br /><span className="Table-Header-Info">(JJ/MM/AAAA)</span></div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.CreationDate)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.CreationDate] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">
                                            <span>Date<br /></span>
                                            <span className="Table-Header-Info">(JJ/MM/AAAA)</span>
                                        </div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.DocumentType)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.DocumentType] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Document</div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.Entity)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.Entity] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Entité</div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.Store)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.Store] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Magasin</div>
                                    </div>
                                </th>
                                <th onClick={() => toggleSort(SortByType.Status)}>
                                    <div className="Table-Header-Container">
                                        {sortInfos.orders[SortByType.Status] ?? false ?
                                            <FaSortAmountUp className="Table-Header-Button-Icone" /> :
                                            <FaSortAmountDown className="Table-Header-Button-Icone" />
                                        }
                                        <div className="Table-Header-Text">Statut</div>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtredFiles.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize).map((file) => (
                                <tr key={file.id} className={file.isRead ? "" : "Table-Body-Status-Not-Seen"}>
                                    <td className="Table-Body-Select" onClick={() => toggleFileSelection(file)} >
                                        <input
                                            type="checkbox"
                                            checked={isSelected(file)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => toggleFileSelection(file)}
                                        />
                                    </td>
                                    <td><img src={getImageLink(file.fileType)} alt={file.fileType} className="File-Type-Icone" /></td>
                                    <td 
                                        className={`Table-Body-File-Name ${loadingFileId === file.id ? 'loading' : ''}`} 
                                        onClick={() => loadingFileId === null && handleOpenFile(file)}
                                    >
                                        {loadingFileId === file.id && (
                                            <FaSpinner className="Table-Body-Spinner spinning" />
                                        )}
                                        {file.name}
                                    </td>
                                    <td>{file.dueDate ? new Date(file.dueDate).toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit" }) : "-"}</td>
                                    <td>{new Date(file.createdAt).toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit" })}
                                    </td>
                                    <td>{file.documentType && file.documentType.trim() !== "" ? file.documentType : "Non connu"}</td>
                                    <td>{file.entityType && file.entityType.trim() !== "" ? file.entityType : "Non connu"}</td>
                                    <td>{file.storeName && file.storeName.trim() !== "" ? file.storeName : "Non connu"}</td>
                                    <td>{file.isRead ? "Lu" : "Non Lu"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                </div>
            </div>
        </>
    )
}

export default DocumentsTable;